/**
 * CrmCustomers - Página de listagem de clientes CRM
 * 
 * Exibe lista de clientes com paginação server-side, filtros e ações
 * Clientes são criados automaticamente quando deals são ganhos
 * Design compacto seguindo padrão do CrmDeals (Todos Negócios)
 * 
 * @version 4.0.0 - Layout compacto padronizado com CrmDeals
 * @author Sistema
 */
import { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';
import { 
  Users, 
  Search, 
  Building2,
  User,
  Phone,
  Mail,
  MoreHorizontal,
  Eye,
  Edit,
  Plus,
  Upload,
  AlertCircle,
  RefreshCw,
  X
} from 'lucide-react';
import { LucideIcon } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

import { Skeleton } from '@/components/ui/skeleton';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { SmartAvatar } from '@/components/ui/smart-avatar';

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ScrollIndicators } from '@/components/ui/scroll-indicators';
import { 
  CustomerType, 
  CrmCustomer 
} from '@/hooks/useCrmCustomers';
import { 
  useCrmCustomersPaginated, 
  PaginatedCustomer 
} from '@/hooks/useCrmCustomersPaginated';
import { CustomerFormDialog } from '@/components/crm/customers/CustomerFormDialog';
// v4.1.0: cadastro automático via integração (ERP) — botão com escolha Manual/Automático
import { NewCustomerButton } from '@/components/crm/customers/NewCustomerButton';
import { CustomerAutoImportDialog } from '@/components/crm/customers/CustomerAutoImportDialog';
import { CustomerDetailsModalPremium } from '@/components/crm/customers/modal/CustomerDetailsModalPremium';
import { PaginationFooter } from '@/components/ui/pagination-footer';
import { getAvatarColorClasses } from '@/lib/utils/avatar-colors';
import { cn } from '@/lib/utils';
import { usePermissions } from '@/hooks/usePermissions';
import { useAutoOpenFromQuery } from '@/hooks/useAutoOpenFromQuery';

// ============================================================================
// FUNÇÕES AUXILIARES
// ============================================================================

/**
 * Obtém as iniciais do nome do cliente (estrutura flat)
 */
function getCustomerInitials(customer: PaginatedCustomer): string {
  const name = customer.customer_type === 'pf' 
    ? customer.nome 
    : customer.razao_social || customer.nome_fantasia;
  
  if (!name) return '??';
  
  return name
    .split(' ')
    .slice(0, 2)
    .map(n => n[0])
    .join('')
    .toUpperCase();
}

/**
 * Obtém o nome de exibição do cliente (estrutura flat)
 */
function getCustomerDisplayName(customer: PaginatedCustomer): string {
  if (customer.customer_type === 'pf') {
    return customer.nome || 'Sem nome';
  }
  return customer.nome_fantasia || customer.razao_social || 'Sem nome';
}

/**
 * Obtém o documento mascarado do cliente (sigilo server-side via RPC v1.0.3)
 */
function getCustomerDocumentMasked(customer: PaginatedCustomer): string | null {
  return customer.customer_type === 'pf' ? customer.cpf_masked : customer.cnpj_masked;
}
/* Badge de status removido: cliente é sempre Ativo */


/**
 * Tipo de cliente exibido inline (sem badge): ícone colorido + sigla.
 * - PF: ícone azul + "PF"
 * - PJ: ícone violeta + "PJ"
 */
function CustomerTypeInline({ type }: { type: CustomerType }) {
  if (type === 'pf') {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs font-medium">
        <User className="h-3.5 w-3.5 text-blue-600" />
        PF
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 text-xs font-medium">
      <Building2 className="h-3.5 w-3.5 text-violet-600" />
      PJ
    </span>
  );
}

// ============================================================================
// COMPONENTE DE CARDS DE MÉTRICAS — PADRÃO HORIZONTAL COMPACTO
// ============================================================================

/** Estilos semânticos para cada variante de KPI card */
const METRIC_CARD_STYLES = {
  primary: { cardBg: 'bg-purple-100 dark:bg-purple-900/30', iconColor: 'text-purple-700 dark:text-purple-300' },
  success: { cardBg: 'bg-lime-100 dark:bg-green-900/30', iconColor: 'text-green-700 dark:text-green-300' },
  info: { cardBg: 'bg-blue-100 dark:bg-blue-900/30', iconColor: 'text-blue-700 dark:text-blue-300' },
  purple: { cardBg: 'bg-cyan-100 dark:bg-cyan-900/30', iconColor: 'text-cyan-700 dark:text-cyan-300' },
} as const;

interface MetricCardProps {
  title: string;
  value: number;
  icon: LucideIcon;
  variant?: keyof typeof METRIC_CARD_STYLES;
}

/** Card de métrica horizontal — ícone+título à esquerda, valor à direita */
function MetricCard({ title, value, icon: Icon, variant = 'primary' }: MetricCardProps) {
  const styles = METRIC_CARD_STYLES[variant];
  return (
    <Card className={cn('border-0 shadow-none transition-all hover:shadow-md', styles.cardBg)}>
      <CardContent className="p-3">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <Icon className={cn('h-5 w-5 shrink-0', styles.iconColor)} />
            <p className="text-xs font-medium truncate">{title}</p>
          </div>
          <p className="text-xl font-bold leading-tight shrink-0">{value}</p>
        </div>
      </CardContent>
    </Card>
  );
}

// ============================================================================
// COMPONENTE PRINCIPAL
// ============================================================================

export default function CrmCustomers() {
  const { t } = useTranslation(['crm', 'common']);

  // === Controle de permissões granular ===
  const { canCreate, canEdit, canDelete, isAdmin } = usePermissions();
  const MENU_KEY = 'crm_customers';
  const hasCreatePermission = canCreate(MENU_KEY);
  const hasEditPermission = canEdit(MENU_KEY);
  const hasDeletePermission = canDelete(MENU_KEY);
  // Permissão para importação em massa (admin/owner ou granular)
  const canImportCustomers = isAdmin || canCreate('crm_customers_import');
  
  // Estados de filtro
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<CustomerType | 'all'>('all');
  
  
  // Estados de paginação
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  
  // Estados de modal
  const [selectedCustomerId, setSelectedCustomerId] = useState<string | null>(null);
  const [editingCustomer, setEditingCustomer] = useState<CrmCustomer | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  // v4.1.0: controla o wizard de cadastro automático (consulta por CPF/CNPJ na integração)
  const [autoImportOpen, setAutoImportOpen] = useState(false);
  // Auto-abre modal quando vindo do botão 'Novo' do header (Quick Create)
  useAutoOpenFromQuery('1', () => setFormOpen(true));

  /**
   * Deep link vindo da Pesquisa Global do header:
   *  - ?customerId=<uuid> abre direto o modal de detalhes do cliente
   *  - ?q=<termo> pré-preenche o filtro de busca da listagem
   * O parâmetro é consumido apenas uma vez (limpo da URL após aplicar).
   */
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const customerIdParam = params.get('customerId');
    const qParam = params.get('q');

    if (customerIdParam) {
      setSelectedCustomerId(customerIdParam);
      setDetailsOpen(true);
    }
    if (qParam) {
      setSearch(qParam);
    }

    if (customerIdParam || qParam) {
      params.delete('customerId');
      params.delete('q');
      const rest = params.toString();
      window.history.replaceState({}, '', window.location.pathname + (rest ? `?${rest}` : ''));
    }
    // Executa apenas na montagem da página
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  // Hook de clientes paginados via RPC
  const { 
    customers, 
    totalCount,
    totalPages,
    isLoading, 
    isFetching,
    error,
    refetch,
  } = useCrmCustomersPaginated(
    {
      customer_type: typeFilter === 'all' ? null : typeFilter,
      lifecycle_stage: null,
      search: search || null,
    },
    { page: currentPage, pageSize }
  );

  // Reset de página quando filtros mudam
  useEffect(() => {
    setCurrentPage(1);
  }, [search, typeFilter]);


  // Métricas calculadas (contadores da página atual + total real)
  const metrics = useMemo(() => {
    return {
      total: totalCount,
      pf: customers.filter(c => c.customer_type === 'pf').length,
      pj: customers.filter(c => c.customer_type === 'pj').length,
    };
  }, [customers, totalCount]);

  // Verifica se há filtros ativos
  const hasActiveFilters = search || typeFilter !== 'all';

  // Limpa todos os filtros
  const clearFilters = () => {
    setSearch('');
    setTypeFilter('all');

  };

  // Handlers de paginação
  const handlePageChange = (page: number) => setCurrentPage(page);
  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  // Handlers de modais
  const handleViewDetails = (customer: PaginatedCustomer) => {
    setSelectedCustomerId(customer.id);
    setDetailsOpen(true);
  };

  const handleEdit = (customer: PaginatedCustomer | CrmCustomer) => {
    // Verifica se é PaginatedCustomer (estrutura flat) ou CrmCustomer (estrutura aninhada)
    const isPaginated = 'main_contact_name' in customer;
    
    // Converte para CrmCustomer para o modal de edição
    const crmCustomer: CrmCustomer = {
      id: customer.id,
      tenant_id: '',
      main_contact_id: isPaginated 
        ? (customer as PaginatedCustomer).main_contact_id || '' 
        : (customer as CrmCustomer).main_contact_id,
      origin_deal_id: null,
      customer_type: customer.customer_type,
      nome: customer.nome,
      cpf: customer.cpf,
      data_nascimento: customer.data_nascimento,
      razao_social: customer.razao_social,
      nome_fantasia: customer.nome_fantasia,
      cnpj: customer.cnpj,
      cep: customer.cep,
      logradouro: customer.logradouro,
      numero: customer.numero,
      complemento: customer.complemento,
      bairro: customer.bairro,
      cidade: customer.cidade,
      estado: customer.estado,
      seller_id: customer.seller_id,
      lifecycle_stage: customer.lifecycle_stage,
      notes: customer.notes,
      metadata: {},
      created_at: customer.created_at,
      updated_at: customer.updated_at,
      main_contact: isPaginated 
        ? {
            id: (customer as PaginatedCustomer).main_contact_id || '',
            name: (customer as PaginatedCustomer).main_contact_name,
            phone: (customer as PaginatedCustomer).main_contact_phone,
            email: (customer as PaginatedCustomer).main_contact_email,
            avatar_url: (customer as PaginatedCustomer).main_contact_avatar,
          } 
        : (customer as CrmCustomer).main_contact,
      seller: isPaginated && customer.seller_id 
        ? {
            id: customer.seller_id,
            name: (customer as PaginatedCustomer).seller_name || '',
            email: (customer as PaginatedCustomer).seller_email,
            phone: (customer as PaginatedCustomer).seller_phone,
            avatar_url: (customer as PaginatedCustomer).seller_avatar,
          } 
        : (customer as CrmCustomer).seller,
    };
    setEditingCustomer(crmCustomer);
    setFormOpen(true);
  };

  const handleCloseForm = () => {
    setFormOpen(false);
    setEditingCustomer(null);
  };

  const handleNewCustomer = () => {
    setEditingCustomer(null);
    setFormOpen(true);
  };

  /** v4.1.0: abre o wizard de cadastro automático (consulta por CPF/CNPJ na integração) */
  const handleAutoImportCustomer = () => setAutoImportOpen(true);


  return (
    <div className="flex flex-col h-full">
      {/* Header Compacto Premium */}
      {/* Mobile: título + ações na mesma linha (subtítulo oculto). Desktop: layout original. */}
      <div className="flex flex-col gap-3 p-4 border-b sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center justify-between gap-2 sm:block">
          <div>
            <h1 className="text-xl font-semibold">Clientes</h1>
          </div>
          {/* Ações mobile — alinhadas à direita na mesma linha do título */}
          <div className="flex items-center gap-2 shrink-0 sm:hidden">
            <Button
              variant="outline"
              size="icon-sm"
              onClick={() => refetch()}
              disabled={isFetching}
              title="Atualizar lista"
            >
              <RefreshCw className={cn('h-3.5 w-3.5', isFetching && 'animate-spin')} />
            </Button>
            {canImportCustomers && (
              <Button
                asChild
                variant="outline"
                size="icon-sm"
                title="Importar Clientes em Massa (CSV)"
              >
                <Link to="/tenant/lists?tab=customers">
                  <Upload className="h-3.5 w-3.5" />
                </Link>
              </Button>
            )}
            {hasCreatePermission && (
              <NewCustomerButton
                onManual={handleNewCustomer}
                onAutomatic={handleAutoImportCustomer}
              />
            )}
          </div>
        </div>
        {/* Ações desktop */}
        <div className="hidden sm:flex items-center gap-2 shrink-0">
          <Button
            variant="outline"
            size="icon-sm"
            onClick={() => refetch()}
            disabled={isFetching}
            title="Atualizar lista"
          >
            <RefreshCw className={cn('h-3.5 w-3.5', isFetching && 'animate-spin')} />
          </Button>
          {canImportCustomers && (
            <Button
              asChild
              variant="outline"
              size="icon-sm"
              title="Importar Clientes em Massa (CSV)"
            >
              <Link to="/tenant/lists?tab=customers">
                <Upload className="h-3.5 w-3.5" />
              </Link>
            </Button>
          )}
          {hasCreatePermission && (
            <NewCustomerButton
              onManual={handleNewCustomer}
              onAutomatic={handleAutoImportCustomer}
            />
          )}
        </div>
      </div>


      {/* KPI Cards em Grid - Igual à página de Categorias */}
      {!isLoading && (
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 px-4 py-3 border-b">
          <MetricCard
            title="Total de Clientes"
            value={metrics.total}
            icon={Users}
            variant="primary"
          />

          <MetricCard
            title="Pessoa Física"
            value={metrics.pf}
            icon={User}
            variant="info"
          />
          <MetricCard
            title="Pessoa Jurídica"
            value={metrics.pj}
            icon={Building2}
            variant="purple"
          />
        </div>
      )}

      {/* Barra de Filtros Premium - Mesmo padrão do CrmDeals */}
      <div className="flex flex-wrap items-center gap-2 p-3 border-b bg-muted/30">
        {/* Busca */}
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            placeholder="Buscar por nome, CPF, CNPJ..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8 h-8 text-sm"
          />
        </div>

        {/* Filtro por tipo */}
        <Select 
          value={typeFilter} 
          onValueChange={(v) => setTypeFilter(v as CustomerType | 'all')}
        >
          <SelectTrigger className="w-[140px] h-8 text-sm">
            <SelectValue placeholder="Tipo" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos tipos</SelectItem>
            <SelectItem value="pf">
              <div className="flex items-center gap-2">
                <User className="h-3.5 w-3.5" /> Pessoa Física
              </div>
            </SelectItem>
            <SelectItem value="pj">
              <div className="flex items-center gap-2">
                <Building2 className="h-3.5 w-3.5" /> Pessoa Jurídica
              </div>
            </SelectItem>
          </SelectContent>
        </Select>
        {/* Filtro por status removido: cliente é sempre Ativo */}


        {/* Botão Limpar Filtros */}
        {hasActiveFilters && (
          <Button
            variant="ghost"
            size="sm"
            onClick={clearFilters}
            className="h-8 px-2 text-xs text-muted-foreground hover:text-foreground"
          >
            <X className="h-3 w-3 mr-1" />
            Limpar
          </Button>
        )}

        {/* Indicador de carregamento */}
        {isFetching && !isLoading && (
          <span className="text-xs text-muted-foreground animate-pulse">
            Atualizando...
          </span>
        )}
      </div>

      {/* Estado de erro */}
      {error && (
        <Alert variant="destructive" className="m-4">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Erro ao carregar clientes</AlertTitle>
          <AlertDescription className="flex items-center justify-between">
            <span className="text-sm">{(error as Error).message || 'Erro desconhecido'}</span>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => refetch()}
              className="ml-4 gap-2"
            >
              <RefreshCw className="h-3 w-3" />
              Tentar novamente
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {/* Tabela Premium - com setinhas de scroll horizontal */}
      <ScrollIndicators className="flex-1">
        <div className="h-full w-full overflow-hidden">
        {isLoading ? (
          <div className="space-y-2 p-4">
            {[1, 2, 3, 4, 5].map(i => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center h-64 gap-3">
            <AlertCircle className="h-12 w-12 text-destructive/40" />
            <div className="text-center">
              <h3 className="font-medium text-sm">Falha ao carregar dados</h3>
              <p className="text-xs text-muted-foreground mt-1">
                Verifique a mensagem de erro acima
              </p>
            </div>
          </div>
        ) : customers.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 gap-3">
            <Users className="h-12 w-12 text-muted-foreground/40" />
            <div className="text-center">
              <h3 className="font-medium text-sm">Nenhum cliente encontrado</h3>
              <p className="text-xs text-muted-foreground mt-1">
                {hasActiveFilters
                  ? 'Tente ajustar os filtros de busca'
                  : 'Clientes são criados quando negócios são ganhos'}
              </p>
            </div>
            {!hasActiveFilters && hasCreatePermission && (
              <Button 
                size="xs"
                onClick={handleNewCustomer}
                className="hover:shadow-md hover:scale-[1.02] transition-all duration-200"
              >
                <Plus className="mr-1.5" />
                Novo Cliente
              </Button>
            )}
          </div>
        ) : (
          <Table scrollViewport containerClassName="h-full" className="min-w-[1400px]">
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="text-xs font-medium">Cliente</TableHead>
                <TableHead className="text-xs font-medium">Tipo</TableHead>
                <TableHead className="text-xs font-medium">Documento</TableHead>
                <TableHead className="text-xs font-medium">Contato</TableHead>
                <TableHead className="text-xs font-medium">Cidade</TableHead>
                <TableHead className="text-xs font-medium">UF</TableHead>
                <TableHead className="text-xs font-medium">Vendedor</TableHead>
                
                <TableHead className="text-xs font-medium">Criado em</TableHead>
                <TableHead className="w-10" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {customers.map((customer) => {
                const initials = getCustomerInitials(customer);
                const displayName = getCustomerDisplayName(customer);
                // Documento e contato vêm mascarados do server (RPC v1.0.3 — sigilo)
                const documentMasked = getCustomerDocumentMasked(customer);
                const colorClasses = getAvatarColorClasses(customer.id);

                return (
                  <TableRow 
                    key={customer.id}
                    className="cursor-pointer hover:bg-muted/40 group"
                    onClick={() => handleViewDetails(customer)}
                  >
                    {/* Nome e contato */}
                    <TableCell className="py-2">
                      <div className="flex items-center gap-2.5">
                        <Avatar className="h-8 w-8">
                          <AvatarImage 
                            src={customer.main_contact_avatar || undefined} 
                          />
                          <AvatarFallback className={cn('text-xs', colorClasses)}>
                            {initials}
                          </AvatarFallback>
                        </Avatar>
                        <div className="min-w-0">
                          <p className="text-sm font-medium truncate">{displayName}</p>
                          {customer.customer_type === 'pj' && customer.nome_fantasia && (
                            <p className="text-xs text-muted-foreground truncate">
                              {customer.razao_social}
                            </p>
                          )}
                        </div>
                      </div>
                    </TableCell>

                    {/* Tipo — ícone + sigla (sem badge) */}
                    <TableCell className="py-2">
                      <CustomerTypeInline type={customer.customer_type} />
                    </TableCell>

                    {/* Documento (mascarado server-side) */}
                    <TableCell className="py-2">
                      <span className="font-mono text-xs text-muted-foreground">
                        {documentMasked || '-'}
                      </span>
                    </TableCell>

                    {/* Contato — telefone e e-mail mascarados server-side */}
                    <TableCell className="py-2">
                      <div className="space-y-0.5">
                        {customer.main_contact_phone_masked && (
                          <div className="flex items-center gap-1 text-xs text-muted-foreground">
                            <Phone className="h-3 w-3" />
                            <span className="font-mono">{customer.main_contact_phone_masked}</span>
                          </div>
                        )}
                        {customer.main_contact_email_masked && (
                          <div className="flex items-center gap-1 text-xs text-muted-foreground">
                            <Mail className="h-3 w-3" />
                            <span className="truncate max-w-[140px]">{customer.main_contact_email_masked}</span>
                          </div>
                        )}
                      </div>
                    </TableCell>

                    {/* Cidade */}
                    <TableCell className="py-2">
                      <span className="text-xs text-muted-foreground truncate max-w-[140px] block">
                        {customer.cidade || '-'}
                      </span>
                    </TableCell>

                    {/* UF */}
                    <TableCell className="py-2">
                      <span className="text-xs text-muted-foreground uppercase">
                        {customer.estado || '-'}
                      </span>
                    </TableCell>

                    {/* Vendedor (estrutura flat) com avatar 3D */}
                    <TableCell className="py-2">
                      {customer.seller_id && customer.seller_name ? (
                        <div className="flex items-center gap-2">
                          <SmartAvatar
                            src={customer.seller_avatar}
                            name={customer.seller_name}
                            size="xs"
                            use3DFallback={true}
                          />
                          <span className="text-xs truncate max-w-[100px]">{customer.seller_name}</span>
                        </div>
                      ) : (
                        <span className="text-muted-foreground text-xs">-</span>
                      )}
                    </TableCell>



                    {/* Criado em */}
                    <TableCell className="py-2">
                      <span className="text-xs text-muted-foreground whitespace-nowrap">
                        {customer.created_at
                          ? format(new Date(customer.created_at), 'dd/MM/yyyy HH:mm', { locale: ptBR })
                          : '-'}
                      </span>
                    </TableCell>

                    {/* Ações - visível apenas no hover */}
                    <TableCell className="py-2" onClick={(e) => e.stopPropagation()}>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <MoreHorizontal className="h-3.5 w-3.5" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleViewDetails(customer)}>
                            <Eye className="h-4 w-4 mr-2" />
                            Ver detalhes
                          </DropdownMenuItem>
                          {hasEditPermission && (
                            <DropdownMenuItem onClick={() => handleEdit(customer)}>
                              <Edit className="h-4 w-4 mr-2" />
                              Editar
                            </DropdownMenuItem>
                          )}
                          {/* Ações de status removidas: cliente é sempre Ativo */}
                        </DropdownMenuContent>

                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
        </div>
      </ScrollIndicators>

      {/* Footer de paginação - Mesmo padrão do CrmDeals */}
      {!isLoading && !error && customers.length > 0 && (
        <div className="border-t px-4 py-2 bg-background">
          <PaginationFooter
            totalItems={totalCount}
            currentPage={currentPage}
            pageSize={pageSize}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            pageSizeOptions={[10, 20, 50, 100]}
            borderClassName=""
            className="mt-0 pt-0"
          />
        </div>
      )}

      {/* Modal de detalhes premium - edição inline ativada */}
      <CustomerDetailsModalPremium
        customerId={selectedCustomerId}
        open={detailsOpen}
        onOpenChange={setDetailsOpen}
      />

      {/* Dialog de edição */}
      <CustomerFormDialog
        customer={editingCustomer}
        open={formOpen}
        onOpenChange={handleCloseForm}
        onSuccess={() => {
          refetch();
          handleCloseForm();
        }}
        // v4.2.0: atalho "Automático" no rodapé reaproveita o wizard já montado nesta tela
        onAutomatic={() => setAutoImportOpen(true)}
        onEditExistingCustomer={(customerId) => {
          // Fecha o modal de criação e abre detalhes do cliente existente
          handleCloseForm();
          setSelectedCustomerId(customerId);
          setDetailsOpen(true);
        }}
      />

      {/* v4.1.0: wizard de cadastro automático via integração externa */}
      <CustomerAutoImportDialog
        open={autoImportOpen}
        onOpenChange={setAutoImportOpen}
        onSuccess={(customerId) => {
          refetch();
          setSelectedCustomerId(customerId);
          setDetailsOpen(true);
        }}
      />
    </div>
  );
}
